/* eslint-disable max-lines */
import dayjs from "dayjs";
import { FC, useEffect, useState } from "react";
import useFetch from "../../hooks/useFetch";
import {
  CREDIT_TYPE_LABELS,
  labelFor,
  PRODUCT_NAME_LABELS
} from "../../interfaces/wallet/Wallet.types";
import {
  getCreditActivity,
  getCreditLiability
} from "../../services/Wallet.service";
import DashboardCard from "../dashboard/DashboardCard";
import RangePicker from "../ui/datepicker/range/RangePicker";
import CreditActivityGraph from "./reports/CreditActivityGraph";
import CreditBars from "./reports/CreditBars";
import CreditDonut from "./reports/CreditDonut";

// The picker hands back an inclusive end date; the activity endpoint filters `InsertDate < toUtc` and
// rejects an equal pair outright. Both ends are therefore stored inclusive — as shown to the admin — and
// only the request is shifted, so "1 - 3 August" really covers 3 August and "Sot" is a valid one-day range.
const exclusiveEnd = (inclusiveEnd: string) =>
  dayjs(inclusiveEnd).add(1, "day").format("YYYY-MM-DD");

const money = (v?: number) => (v ?? 0).toFixed(2);

const PRODUCT_COLORS: Record<string, string> = {
  Esim: "#2D87F1",
  Travel: "#F15B2D"
};

const SCOPE_OPTIONS = [
  { value: "", label: "Të gjitha" },
  { value: "Any", label: "Bileta ose eSIM" },
  { value: "TravelOnly", label: "Vetëm bileta" },
  { value: "EsimOnly", label: "Vetëm eSIM" }
];

const LoadFailed: FC<{ onRetry: () => void }> = ({ onRetry }) => (
  <p className="text-sm text-red mb-3">
    Raporti nuk u ngarkua dot.{" "}
    <button type="button" className="underline" onClick={onRetry}>
      Provo përsëri
    </button>
  </p>
);

const CreditReports: FC = () => {
  const {
    response: liability,
    loading: liabilityLoading,
    error: liabilityError,
    handleFunc: loadLiability
  } = useFetch(getCreditLiability, { initRequest: true });
  // resetResponseOnError: a failed range must not leave the previous range's figures on screen.
  const {
    response: activity,
    loading: activityLoading,
    error: activityError,
    handleFunc: loadActivity
  } = useFetch(getCreditActivity, { loading: true, resetResponseOnError: true });

  const [from, setFrom] = useState(
    dayjs().subtract(30, "day").format("YYYY-MM-DD")
  );
  const [to, setTo] = useState(dayjs().format("YYYY-MM-DD"));
  const [scope, setScope] = useState("");

  const reloadActivity = () =>
    void loadActivity({ from, to: exclusiveEnd(to), scope });

  useEffect(reloadActivity, []);

  const l = liability?.data;
  const a = activity?.data;

  return (
    <div className="px-5 pb-8">
      <h2 className="text-lg font-semibold mb-3">Kredite të papërdorura</h2>

      {!!liabilityError && !liabilityLoading && (
        <LoadFailed onRetry={() => void loadLiability(undefined)} />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <DashboardCard
          title="Gjithsej"
          info="Kredite që klientët i kanë ende në llogari dhe nuk kanë skaduar."
          currentValue={money(l?.totalOutstanding)}
          ratePercentageDesc=""
          delay={100}
          loading={liabilityLoading}
        />
        <DashboardCard
          title="Klientë me kredite"
          info="Numri i klientëve që kanë kredite të papërdorura."
          currentValue={String(l?.walletsWithBalance ?? 0)}
          ratePercentageDesc=""
          delay={200}
          loading={liabilityLoading}
          currency={false}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
        <CreditDonut
          title="Prejardhja"
          info={
            "Nga rimbursimet: para që klientit i detyroheshin dhe u kthyen si kredite. " +
            "Dhurata: kredite që klienti i fitoi ose ia dhamë ne."
          }
          loading={liabilityLoading}
          data={[
            { name: "Nga rimbursimet", value: l?.fromRefundsOutstanding ?? 0, color: "#2D87F1" },
            { name: "Dhurata", value: l?.promotionalOutstanding ?? 0, color: "#F15B2D" }
          ]}
        />
        <CreditDonut
          title="Ku mund të shpenzohen"
          info="Ku mund t'i përdorë klienti kreditet që i ka ende."
          loading={liabilityLoading}
          data={[
            { name: "Bileta ose eSIM", value: l?.flexible ?? 0, color: "#2D87F1" },
            { name: "Vetëm bileta", value: l?.spendableOnTravelOnly ?? 0, color: "#F15B2D" },
            { name: "Vetëm eSIM", value: l?.spendableOnEsimOnly ?? 0, color: "#36B37E" }
          ]}
        />
      </div>

      <div
        className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mt-8 mb-4
        relative z-30"
      >
        <h2 className="text-lg font-semibold">Aktiviteti</h2>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center">
            <label
              htmlFor="credit-activity-scope"
              className="mb-2 sm:mb-0 sm:mr-2 text-sm font-medium text-gray-700 whitespace-nowrap"
            >
              Ku shpenzohen:
            </label>
            <select
              id="credit-activity-scope"
              value={scope}
              onChange={(e) => {
                setScope(e.target.value);
                void loadActivity({ from, to: exclusiveEnd(to), scope: e.target.value });
              }}
              className="w-full sm:w-44 px-3 py-2 border border-gray-300 rounded-md shadow-sm
                focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-white text-sm"
            >
              {SCOPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center">
            <label className="mb-2 sm:mb-0 sm:mr-2 text-sm font-medium text-gray-700 whitespace-nowrap">
              Filtro datën:
            </label>
            <div className="w-full sm:w-64">
              <RangePicker
                className="flex flex-row items-center w-full"
                range={{
                  from: dayjs(from).toDate(),
                  to: dayjs(to).toDate()
                }}
                pickerProps={{ toDate: dayjs().add(1, "day").toDate() }}
                hasHelpers={true}
                onApply={(r) => {
                  if (!r?.from) return;

                  const nextFrom = dayjs(r.from).format("YYYY-MM-DD");
                  // A half-picked range is a single day, not an open end.
                  const nextTo = dayjs(r.to ?? r.from).format("YYYY-MM-DD");

                  setFrom(nextFrom);
                  setTo(nextTo);
                  void loadActivity({ from: nextFrom, to: exclusiveEnd(nextTo), scope });
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {!!activityError && !activityLoading && (
        <LoadFailed onRetry={reloadActivity} />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <DashboardCard
          title="Të lëshuara"
          info="Kredite të dhëna në këtë periudhë: dhurata dhe rimbursime si kredite."
          currentValue={money(a?.issued)}
          ratePercentageDesc=""
          delay={100}
          loading={activityLoading}
        />
        <DashboardCard
          title="Të shpenzuara"
          info="Kredite që klientët i përdorën për pagesa në këtë periudhë."
          currentValue={money(a?.redeemed)}
          ratePercentageDesc=""
          delay={200}
          loading={activityLoading}
        />
        <DashboardCard
          title="Të skaduara"
          info="Kredite që skaduan pa u përdorur."
          currentValue={money(a?.expired)}
          ratePercentageDesc=""
          delay={300}
          loading={activityLoading}
        />
        <DashboardCard
          title="Të kthyera"
          info="Kredite të shpenzuara që iu kthyen klientit pas një anulimi."
          currentValue={money(a?.returned)}
          ratePercentageDesc=""
          delay={400}
          loading={activityLoading}
        />
      </div>

      {(activityLoading || a?.daily) && (
        <CreditActivityGraph data={a?.daily} loading={activityLoading} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4 items-start">
        <CreditBars
          title="Nga çfarë erdhën"
          info="Kreditet e lëshuara në këtë periudhë, sipas burimit."
          loading={activityLoading}
          className="lg:col-span-2"
          data={Object.entries(a?.issuedByType ?? {}).map(([type, value]) => ({
            name: labelFor(CREDIT_TYPE_LABELS, type),
            value
          }))}
        />
        <CreditDonut
          title="Ku u shpenzuan"
          info="Kreditet e shpenzuara në këtë periudhë, sipas produktit."
          loading={activityLoading}
          data={Object.entries(a?.redeemedByProduct ?? {}).map(
            ([product, value]) => ({
              name:
                product === "Unspecified"
                  ? "Të tjera"
                  : labelFor(PRODUCT_NAME_LABELS, product),
              value,
              color: PRODUCT_COLORS[product] ?? "#36B37E"
            })
          )}
        />
      </div>
    </div>
  );
};

export default CreditReports;
